import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { QRCodeCanvas } from "qrcode.react";
import { ChangeEvent, useEffect, useState } from "react";

import { LoadingOutlined } from "@ant-design/icons";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import { setCountry, setOrigin } from "../../features/map/mapSlice";
import {
  createAdminQr,
  deleteAdminQr,
  getAdminQrByAdminId,
  updateAdminQr,
} from "../../services/AdminQrApi";
import { AdminQrDTO } from "../../shared/dtos/adminQrDTO";
import PatchAdminQrDTO from "../../shared/dtos/PatchAdminQrDTO";
import { COUNTRIES } from "../../utils/constants/countries";
import {
  selectCurrentAdminId,
  selectCurrentAdminQrOptionalAddressAllowed,
} from "../../utils/helpers/selectorHelper";
import ContainerLayout from "../common/containerLayout";
import DatePicker from "../common/DatePicker";
import ComponentPlacesAutocomplete from "../map/PlaceAutocomplete";
import mowi from "./mowi.png";

const { Text } = Typography;

const qrUrlValue = process.env.REACT_APP_QR_CODE_URL;

const initialFormValues: AdminQrDTO = {
  id: undefined,
  code: "",
  qrSize: 256,
  imgQrSize: 32,
  bgColor: "#FFFFFF",
  fgColor: "#000000",
  errorLevel: "M",
  countryId: COUNTRIES[0].value,
  isExpired: false,
};

const GenerateQR = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<AdminQrDTO>();
  const [loading, setLoading] = useState(false);
  const [addressInputKey, setAddressInputKey] = useState(0);
  const { destinations, country } = useAppSelector(
    (state: RootState) => state?.map,
  );
  const optionalAddressAllowed = selectCurrentAdminQrOptionalAddressAllowed();

  Form.useWatch("code", form);
  Form.useWatch("qrSize", form);
  Form.useWatch("bgColor", form);
  Form.useWatch("imgQrSize", form);
  Form.useWatch("fgColor", form);
  Form.useWatch("errorLevel", form);
  Form.useWatch("expiration", form);
  Form.useWatch("countryId", form);
  const addressValue = Form.useWatch("address", form);
  const qrId = Form.useWatch("id", form);

  const addressText =
    addressValue && typeof addressValue === "object"
      ? (addressValue as { label?: string }).label
      : (addressValue as string | undefined);

  const currentOrigin = destinations?.pickUp?.originAddress ?? addressText;
  const isExpired = Form.useWatch("isExpired", form);

  const downloadQRCode = () => {
    const canvas = document.querySelector("#qr-code") as HTMLCanvasElement;
    if (!canvas) throw new Error("<canvas> not found in the DOM");

    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = "QR code.png";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  useEffect(() => {
    const getQr = async () => {
      try {
        setLoading(true);

        const adminId = selectCurrentAdminId();

        const response: AdminQrDTO | undefined =
          await getAdminQrByAdminId(adminId);

        const values = response?.id ? response : initialFormValues;

        const expirationDate = values.expiration
          ? dayjs(values.expiration)
          : null;

        form.setFieldsValue({
          ...values,
          ...(expirationDate
            ? { expiration: expirationDate }
            : { expiration: dayjs().add(30, "day") }),
          isExpired: expirationDate ? expirationDate.isBefore(dayjs()) : false,
        });

        // country
        const country =
          COUNTRIES.find((x) => x.value === values.countryId) ?? COUNTRIES[0];

        dispatch(setCountry(country));
      } catch (error) {
        form.setFieldsValue(initialFormValues);
        dispatch(setCountry(COUNTRIES[0]));
      } finally {
        setLoading(false);
      }
    };

    getQr();
  }, [dispatch, form]);

  const update = async () => {
    try {
      if (!qrId) return;

      const patchAdminQr: PatchAdminQrDTO = new PatchAdminQrDTO();

      patchAdminQr.address = destinations?.pickUp?.originAddress;
      patchAdminQr.longitude = destinations?.pickUp?.originLongitude;
      patchAdminQr.latitude = destinations?.pickUp?.originLatitude;
      patchAdminQr.fee = form.getFieldValue("fee");
      patchAdminQr.countryId = country.value;

      await updateAdminQr(patchAdminQr);

      form.setFieldsValue({
        address: patchAdminQr.address,
        longitude: patchAdminQr.longitude,
        latitude: patchAdminQr.latitude,
        fee: patchAdminQr.fee,
      });

      message.success("QR actualizado satisfactoriamente.");
    } catch (error: any) {
      if (error.response.status === 409)
        message.error("Ya existe un QR para este cliente.");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!optionalAddressAllowed && !destinations?.pickUp) return;
    try {
      setLoading(true);

      form.setFieldsValue({
        address: destinations?.pickUp?.originAddress,
        longitude: destinations?.pickUp?.originLongitude,
        latitude: destinations?.pickUp?.originLatitude,
        adminId: selectCurrentAdminId(),
      });

      const response = await createAdminQr(form.getFieldsValue(true));
      const expirationDate = dayjs(response.expiration);
      form.setFieldsValue({ ...response, expiration: expirationDate });

      message.success("QR generado satisfactoriamente.");
    } catch (error: any) {
      if (error.response.status === 409)
        message.error("Ya existe un QR para este cliente.");
    } finally {
      setLoading(false);
    }
  };

  const clearAddress = async () => {
    dispatch(setOrigin(undefined));
    form.setFieldsValue({
      address: undefined,
      longitude: undefined,
      latitude: undefined,
    });
    setAddressInputKey((key) => key + 1);

    if (!qrId) return;

    try {
      setLoading(true);

      const patchAdminQr: PatchAdminQrDTO = new PatchAdminQrDTO();
      patchAdminQr.address = null;
      patchAdminQr.longitude = null;
      patchAdminQr.latitude = null;
      patchAdminQr.fee = form.getFieldValue("fee");
      patchAdminQr.countryId = country.value;

      await updateAdminQr(patchAdminQr);

      message.success("Dirección eliminada satisfactoriamente.");
    } catch (error) {
      message.error("Ha ocurrido un error al eliminar la dirección.");
    } finally {
      setLoading(false);
    }
  };

  const deleteQRCode = async () => {
    if (qrId) {
      try {
        setLoading(true);
        await deleteAdminQr(qrId);
        const expirationDate = dayjs().add(30, "day");
        form.setFieldsValue({
          ...initialFormValues,
          expiration: expirationDate,
        });
        message.success("El QR se ha eliminado satisfactoriamente.");
      } catch (error) {
        message.error("Ha ocurrido un error al imntentar eliminar el QR.");
      } finally {
        setLoading(false);
      }
    }
  };

  const onChange = (value: any, name: string) => {
    switch (name) {
      case "countryId":
        const country = COUNTRIES.find((x) => x.value === value);
        dispatch(setCountry(country!));
        break;
      case "qrSize":
        const imgQrSize = (15 * +value) / 100;
        form.setFieldValue("imgQrSize", imgQrSize);
        break;
      default:
        break;
    }
  };

  return (
    <ContainerLayout title="Generar código QR" showNotification={false}>
      {loading && (
        <Col span={24} className="loading-container error-container">
          <LoadingOutlined />
        </Col>
      )}
      {!loading && (
        <Col span={24}>
          <Row gutter={[32, 16]} justify="center">
            <Col span={6}>
              <Form
                name="adminQr"
                form={form}
                onFinish={!qrId ? save : update}
                layout="vertical"
              >
                <Form.Item name="id" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="isExpired" hidden>
                  <Input />
                </Form.Item>
                {!qrId && (
                  <Form.Item
                    name="qrSize"
                    label="Tamaño"
                    rules={[{ required: true, message: "Ingrese un tamaño" }]}
                  >
                    <Input
                      type="number"
                      placeholder="356"
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        onChange(event.target.value, "qrSize")
                      }
                    />
                  </Form.Item>
                )}

                {!qrId && (
                  <Form.Item
                    name="errorLevel"
                    label="Nivel de corrección"
                    rules={[
                      {
                        required: true,
                        message: "Ingrese un nivel de corrección",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Seleccine el nivel de corrección"
                      options={[
                        {
                          value: "L",
                          label: "L",
                        },
                        {
                          value: "M",
                          label: "M",
                        },
                        {
                          value: "Q",
                          label: "Q",
                        },
                        {
                          value: "H",
                          label: "H",
                        },
                      ]}
                    />
                  </Form.Item>
                )}

                {!qrId && (
                  <Form.Item
                    name="expiration"
                    label="Fecha expiración"
                    rules={[
                      {
                        required: true,
                        message: "Ingrese una fecha de expiración",
                      },
                    ]}
                  >
                    <DatePicker
                      format={"DD-MM-YYYY"}
                      style={{
                        width: "100%",
                      }}
                    />
                  </Form.Item>
                )}

                {!qrId && (
                  <Form.Item
                    name="fgColor"
                    label="Color"
                    rules={[{ required: true, message: "Ingrese un color" }]}
                  >
                    <Input type="color" />
                  </Form.Item>
                )}

                <Form.Item
                  name="fee"
                  label="Comisión"
                  rules={[{ required: true, message: "Ingrese una comisión" }]}
                >
                  <Input type="number" placeholder="356" min={1} />
                </Form.Item>

                <Form.Item
                  name="countryId"
                  label="País"
                  rules={[{ required: true, message: "Ingrese un país" }]}
                >
                  <Select
                    placeholder="Selecione un país"
                    options={COUNTRIES}
                    showSearch
                    optionFilterProp="label"
                    onChange={(value: number) => onChange(value, "countryId")}
                  />
                </Form.Item>

                <Form.Item
                  name="address"
                  label={
                    optionalAddressAllowed ? "Origen (opcional): " : "Origen: "
                  }
                  rules={
                    optionalAddressAllowed || destinations?.pickUp
                      ? [{ required: false }]
                      : [
                          {
                            required: true,
                            message: "Inserte una dirección!",
                          },
                        ]
                  }
                >
                  <ComponentPlacesAutocomplete
                    key={addressInputKey}
                    type="origin"
                    placeholder="Buscar direccion"
                  />
                </Form.Item>

                <Text style={{ display: "block", marginBottom: 16 }}>
                  Origen actual:{" "}
                  {currentOrigin ? (
                    <Text strong>{currentOrigin}</Text>
                  ) : (
                    <Text type="secondary">Sin origen</Text>
                  )}
                </Text>

                {optionalAddressAllowed && (
                  <Form.Item>
                    <Button
                      danger
                      onClick={clearAddress}
                      loading={loading}
                      block
                    >
                      Limpiar dirección
                    </Button>
                  </Form.Item>
                )}

                <Button type="primary" htmlType="submit" loading={loading}>
                  {!qrId ? "Generar" : "Editar"} QR
                </Button>
              </Form>
            </Col>
            <Col span={6}>
              <Space direction="vertical">
                {qrId && isExpired && (
                  <Alert message="El Código QR ha expirado." type="error" />
                )}
                {qrId && !isExpired && (
                  <Alert
                    message={`Fecha de expiración ${dayjs(
                      form.getFieldValue("expiration"),
                    ).format("DD-MM-YYYY HH:mm:ss")}`}
                    type="info"
                  />
                )}
                <QRCodeCanvas
                  id="qr-code"
                  value={`${qrUrlValue}${form.getFieldValue("code")}`}
                  size={form.getFieldValue("qrSize")}
                  bgColor={form.getFieldValue("bgColor")}
                  fgColor={form.getFieldValue("fgColor")}
                  level={form.getFieldValue("errorLevel")}
                  includeMargin={false}
                  hidden={qrId && isExpired ? true : false}
                  imageSettings={{
                    src: mowi,
                    x: undefined,
                    y: undefined,
                    height: form.getFieldValue("imgQrSize"),
                    width: form.getFieldValue("imgQrSize"),
                    excavate: true,
                  }}
                />
                {!qrId && (
                  <Col span={24}>
                    <Alert
                      message={
                        <>
                          <Col span={24} style={{ textAlign: "center" }}>
                            <Text>Vista previa del QR</Text>
                          </Col>
                          <Col span={24} style={{ textAlign: "center" }}>
                            <Text strong>No válido para ser usado!</Text>
                          </Col>
                        </>
                      }
                      type="warning"
                    />
                  </Col>
                )}
                <Space>
                  {qrId && !isExpired && (
                    <Button type="primary" onClick={downloadQRCode}>
                      Descargar
                    </Button>
                  )}

                  {qrId && (
                    <Button onClick={deleteQRCode} loading={loading}>
                      Eliminar
                    </Button>
                  )}
                </Space>
              </Space>
            </Col>
          </Row>
        </Col>
      )}
    </ContainerLayout>
  );
};

export default GenerateQR;
